'use client';

import { type ReactNode, useState } from 'react';
import { Button } from '@accure/ui/components/button/button-catalyst';
import { InfoIcon } from 'lucide-react';

import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';

export type IntroBoxProps = {
  /**
   * Title displayed in the dialog
   */
  dialogTitle: string;

  /**
   * Short description displayed below the dialog title
   */
  dialogDescription: string;

  /**
   * Main title for the intro box (bold text at the beginning)
   */
  title: string;

  /**
   * Optional header that appears above the main content
   */
  header?: string;

  /**
   * Main content of the intro box
   */
  children: ReactNode;

  /**
   * Detailed content displayed in the dialog when "Learn more" is clicked
   */
  detailedContent: ReactNode;
};

export function IntroBox({
  dialogTitle,
  dialogDescription,
  title,
  header,
  children,
  detailedContent,
}: IntroBoxProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Card className="mb-4 rounded-xl border bg-muted p-4 text-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-2">
            <InfoIcon className="text-primary size-5" />
            <div>
              {header && <p className="text-primary font-medium">{header}</p>}
              <p className="mt-1 text-muted-foreground">
                <strong>{title}</strong> {children}
              </p>
            </div>
          </div>

          <Button
            plain
            className="hover:text-primary mt-2 h-auto whitespace-nowrap px-0 text-xs font-medium text-muted-foreground underline underline-offset-2 transition-colors hover:bg-primary-50 sm:mt-0"
            onClick={() => setIsOpen(true)}
          >
            Learn more
          </Button>
        </div>
      </Card>

      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogDescription>{dialogDescription}</DialogDescription>
        <DialogBody className="space-y-4 text-sm">{detailedContent}</DialogBody>

        <DialogActions>
          <Button onClick={() => setIsOpen(false)}>Got it</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
